import {
  type Dispatch,
  type SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

export enum MicrophonePermissionStatus {
  Denied = "denied",
  Granted = "granted",
  Prompt = "prompt",
}

export interface RecordingOutput {
  blob: Blob;
  duration: number;
  srcUrl: string;
}

// The orchestrator presigns the upload URL for `audio/webm` with a `.webm`
// extension. Sending bytes with a different Content-Type breaks the signed
// PUT contract (and would store non-webm bytes under a `.webm` key even if
// the PUT succeeded). Restrict recording to webm/opus and surface a clear
// "not supported" error for browsers that lack it (Safari < ~16.4). Justen's
// SRN-761 P1 review fix.
export const VOICE_RECORDING_MIME = "audio/webm;codecs=opus";

export class VoiceRecordingNotSupportedError extends Error {
  constructor() {
    super(
      "Your browser doesn't support voice recording in the format we need (webm/opus). Please try a recent Chrome, Edge, or Firefox.",
    );
    this.name = "VoiceRecordingNotSupportedError";
  }
}

export const isVoiceRecordingSupported = (): boolean => {
  if (typeof MediaRecorder === "undefined") {
    return false;
  }
  return MediaRecorder.isTypeSupported(VOICE_RECORDING_MIME);
};

class MicrophoneRecorder {
  private chunks: Blob[] = [];
  private recorder?: MediaRecorder;
  private _stream?: MediaStream;
  private currentAudioUrl?: string;

  get stream(): MediaStream | undefined {
    return this._stream;
  }

  private cleanupStream = () => {
    if (this._stream) {
      this._stream.getTracks().forEach((track) => track.stop());
      this._stream = undefined;
    }
  };

  private cleanupRecorderAndChunks = () => {
    if (this.recorder) {
      this.recorder.ondataavailable = null;
    }
    this.recorder = undefined;
    this.chunks = [];
  };

  constructor(
    private setPermissionState: Dispatch<
      SetStateAction<MicrophonePermissionStatus>
    >,
  ) {}

  requestMedia = async (deviceId?: string) => {
    if (this._stream) {
      this.cleanupStream();
    }

    try {
      const audioConstraints: boolean | MediaTrackConstraints = deviceId
        ? { deviceId: { exact: deviceId } }
        : true;
      this._stream = await navigator.mediaDevices.getUserMedia({
        audio: audioConstraints,
      });
      this.setPermissionState(MicrophonePermissionStatus.Granted);
    } catch (error) {
      this._stream = undefined;
      // Safari/WebKit doesn't reliably fire PermissionStatus change events,
      // so we infer permission state from the getUserMedia rejection name.
      if (error instanceof Error && error.name === "NotAllowedError") {
        this.setPermissionState(MicrophonePermissionStatus.Denied);
      } else {
        this.setPermissionState(MicrophonePermissionStatus.Prompt);
      }
    }
  };

  startRecording = async (isRetry = false): Promise<void> => {
    if (isRetry || !this._stream) {
      this.cleanup();
      this.setPermissionState(MicrophonePermissionStatus.Prompt);
      await this.requestMedia();
    }

    if (!this._stream) {
      throw new Error("Unable to initialize stream before beginning recording");
    }

    this.cleanupRecorderAndChunks();

    if (!isVoiceRecordingSupported()) {
      throw new VoiceRecordingNotSupportedError();
    }
    this.recorder = new MediaRecorder(this._stream, {
      mimeType: VOICE_RECORDING_MIME,
    });

    this.recorder.ondataavailable = (e: BlobEvent) => {
      this.chunks = this.chunks.concat(e.data);
    };

    try {
      this.recorder.start();
    } catch (error) {
      if (isRetry) {
        throw error;
      }
      // A user manually toggling permissions can leave the stream in a bad state;
      // retry once with a fresh stream + recorder.
      return this.startRecording(true);
    }
  };

  getAudio = async (): Promise<RecordingOutput> => {
    const audioContext = new AudioContext();
    try {
      const blob = new Blob(this.chunks, { type: VOICE_RECORDING_MIME });
      const arrayBuffer = await blob.arrayBuffer();
      const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
      const { duration } = audioBuffer;

      if (this.currentAudioUrl) {
        URL.revokeObjectURL(this.currentAudioUrl);
      }
      this.currentAudioUrl = URL.createObjectURL(blob);

      return { blob, duration, srcUrl: this.currentAudioUrl };
    } finally {
      audioContext.close();
    }
  };

  stopRecording = (): Promise<RecordingOutput> => {
    return new Promise((resolve, reject) => {
      if (!this.recorder) {
        return reject(
          new Error("Attempted to stop recording before initialization."),
        );
      }
      if (this.recorder.state !== "recording") {
        return reject(new Error("No recording currently in progress"));
      }

      this.recorder.onstop = async () => {
        try {
          const audio = await this.getAudio();
          resolve(audio);
        } catch {
          reject(new Error("There was a problem parsing the recorded audio."));
        }
      };
      this.recorder.stop();
    });
  };

  cleanup = () => {
    this.cleanupStream();
    this.cleanupRecorderAndChunks();
    if (this.currentAudioUrl) {
      URL.revokeObjectURL(this.currentAudioUrl);
      this.currentAudioUrl = undefined;
    }
  };
}

export const useMicrophone = (
  onReset: () => void,
  setPermissionState: Dispatch<SetStateAction<MicrophonePermissionStatus>>,
) => {
  const [recordingDuration, setRecordingDuration] = useState(0);
  const [stream, setStream] = useState<MediaStream | undefined>();

  // AS-1805: Mantine 7.11.x `useInterval` keys its setup effect on the
  // callback reference (`[fn, active, interval]`), so a fresh closure on
  // every render tears down and re-creates the interval before any tick
  // fires — the recording timer never advances. Replaced with a vanilla
  // setInterval keyed on a stable boolean so the effect only re-runs when
  // recording starts or stops.
  const [isRecording, setIsRecording] = useState(false);
  const intervalRef = useRef<number | null>(null);

  useEffect(() => {
    if (!isRecording) {
      return;
    }
    intervalRef.current = window.setInterval(() => {
      setRecordingDuration((s) => s + 1);
    }, 1000);
    return () => {
      if (intervalRef.current !== null) {
        window.clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [isRecording]);

  const startInterval = useCallback(() => setIsRecording(true), []);
  const stopInterval = useCallback(() => setIsRecording(false), []);

  const recorder = useMemo(
    () => new MicrophoneRecorder(setPermissionState),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const syncStream = useCallback(() => {
    setStream(recorder.stream);
  }, [recorder]);

  const startRecording = useCallback(async () => {
    // Reset duration so a retry inside the same Voice instance (after
    // RecordingFailed → ReadyToRecord) starts the countdown from zero.
    setRecordingDuration(0);
    await recorder.startRecording();
    startInterval();
    syncStream();
  }, [recorder, startInterval, syncStream]);

  const stopRecording = useCallback(() => {
    stopInterval();
    return recorder.stopRecording();
  }, [recorder, stopInterval]);

  const requestPermissions = useCallback(
    async (deviceId?: string) => {
      await recorder.requestMedia(deviceId);
      syncStream();
    },
    [recorder, syncStream],
  );

  const reset = useCallback(
    () =>
      stopRecording()
        .catch(() => {})
        .finally(() => {
          setRecordingDuration(0);
          recorder.cleanup();
          setStream(undefined);
          onReset();
        }),
    [onReset, recorder, stopRecording],
  );

  useEffect(() => {
    return recorder.cleanup;
  }, [recorder]);

  useEffect(() => {
    requestPermissions();
  }, [requestPermissions]);

  return {
    duration: recordingDuration,
    isRecording,
    requestPermissions,
    reset,
    startRecording,
    stopRecording,
    stream,
  };
};
